/** Ticket inquiry flow v2 — fast intake, pricing deferred to quotation stage. */
import { todayISO, fmtDate, esc, fmtAmt } from './utils.js';
import { prepareFlowSteps } from './terminal-flow-merge.js';
import { injectCustomSteps } from './terminal-flow-custom.js';
import { mergeCustomFieldsIntoExtra } from './terminal-flow-handlers.js';
import {
  capitalizeName,
  isSkip,
  parseBaggage,
  parseTransit,
  parseTripType,
  normalizeTripType,
  parseMultiCityRoute,
} from './assistant-ticket-new.js';
import { parseSmartDateOnly, validateSmartDate, DATE_HINT } from './assistant-datetime.js';

export const BOOKING_SYSTEMS = ['TicketGadget', 'Metro', 'NDC', 'Other'];

/** Parse "2/1/0" → { adt, chd, inf } (≥1 adult). */
export function parseTicketPaxLine(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const parts = s.split(/[/,\s]+/).filter(Boolean);
  if (!parts.length) return null;
  const adt = Number(parts[0]);
  if (!Number.isInteger(adt) || adt < 1) return null;
  const chd = parts.length > 1 ? Number(parts[1]) || 0 : 0;
  const inf = parts.length > 2 ? Number(parts[2]) || 0 : 0;
  return { adt, chd: Math.max(0, chd), inf: Math.max(0, inf), stu: 0 };
}

export function initNewTicketDataV2() {
  return {
    type: 'ticket',
    _v2: true,
    name: '',
    phone: '',
    source: '',
    inquiry_date: todayISO(),
    trip_type: 'one_way',
    origin: '',
    destination: '',
    depart_date: '',
    return_date: null,
    preferences: { baggage: null, transit: 'direct', transit_via: null, multi_city_route: null },
    routing_segments: null,
    pax: { adt: 1, stu: 0, chd: 0, inf: 0 },
  };
}

function isSimpleRouting(data) {
  const t = data.preferences?.transit;
  return t === 'direct' || t === 'transit';
}

export function buildNewTicketStepsV2(data, helpers = {}) {
  const { matchSource = (v) => v, formatSourceQuestion = () => 'Lead source?' } = helpers;
  return [
    {
      key: 'inquiry_date',
      ask: 'Customer inquiry date?',
      hint: `${DATE_HINT} · enter for today`,
      optional: true,
      validate: (v) => (isSkip(v) ? null : validateSmartDate(v)),
      parse: (v) => (isSkip(v) ? todayISO() : parseSmartDateOnly(v)),
      apply: (val) => { data.inquiry_date = val; },
    },
    {
      key: 'name',
      ask: 'Contact name?',
      validate: (v) => {
        if (!String(v).trim() || String(v).trim().length < 2) return 'Enter at least 2 characters.';
        return null;
      },
      parse: (v) => capitalizeName(v),
      apply: (val) => { data.name = val; },
    },
    {
      key: 'phone',
      ask: 'Customer mobile number?',
      hint: '+94 77 123 4567 or 0771234567',
      validate: (v) => {
        const phone = helpers.normalizePhone?.(v) || v;
        if (!phone || String(phone).replace(/\D/g, '').length < 9) return 'Enter a valid phone number.';
        return null;
      },
      parse: (v) => (helpers.normalizePhone ? helpers.normalizePhone(v) : v.trim()),
      apply: (val) => { data.phone = val; },
    },
    {
      key: 'source',
      ask: formatSourceQuestion,
      validate: (v) => (!matchSource(v) ? 'Pick a source.' : null),
      parse: (v) => matchSource(v),
      apply: (val) => { data.source = val; },
    },
    {
      key: 'pref_transit',
      ask: 'Flight routing?',
      hint: '1 Direct · 2 Transit · 3 Multi-city',
      validate: (v) => (!parseTransit(v) ? 'Reply 1, 2, or 3.' : null),
      parse: (v) => parseTransit(v),
      apply: (val) => {
        data.preferences.transit = val;
        if (val !== 'transit') data.preferences.transit_via = null;
        if (val !== 'multi') {
          data.preferences.multi_city_route = null;
          data.routing_segments = null;
        }
        if (val === 'multi') data.trip_type = 'multi';
      },
    },
    {
      key: 'pref_trip_type',
      ask: 'Trip type?',
      hint: '1 One way · 2 Return',
      when: () => isSimpleRouting(data),
      validate: (v) => (!parseTripType(v) ? 'Reply 1 or 2.' : null),
      parse: (v) => parseTripType(v),
      apply: (val) => { data.trip_type = val; },
    },
    {
      key: 'origin',
      ask: 'From airport (IATA)?',
      hint: 'e.g. CMB',
      when: () => data.preferences.transit !== 'multi',
      validate: (v) => (/^[A-Za-z]{3}$/.test(String(v).trim()) ? null : 'Enter 3-letter airport code.'),
      parse: (v) => String(v).trim().toUpperCase(),
      apply: (val) => { data.origin = val; },
    },
    {
      key: 'destination',
      ask: 'To airport (IATA)?',
      hint: 'e.g. DXB',
      when: () => data.preferences.transit !== 'multi',
      validate: (v) => (/^[A-Za-z]{3}$/.test(String(v).trim()) ? null : 'Enter 3-letter airport code.'),
      parse: (v) => String(v).trim().toUpperCase(),
      apply: (val) => { data.destination = val; },
    },
    {
      key: 'pref_transit_via',
      ask: 'Preferred transit airport (IATA)?',
      hint: 'e.g. DOH · skip if any',
      when: () => data.preferences.transit === 'transit',
      optional: true,
      validate: (v) => {
        if (isSkip(v)) return null;
        return /^[A-Za-z]{3}$/.test(String(v).trim()) ? null : 'Enter 3-letter code or skip.';
      },
      parse: (v) => (isSkip(v) ? null : String(v).trim().toUpperCase()),
      apply: (val) => { data.preferences.transit_via = val; },
    },
    {
      key: 'pref_multi_city',
      ask: 'Multi-city route — airports in order?',
      hint: 'e.g. CMB SIN DXB CMB',
      when: () => data.preferences.transit === 'multi',
      validate: (v) => (!parseMultiCityRoute(v) ? 'Enter airports separated by spaces (min. 3).' : null),
      parse: (v) => parseMultiCityRoute(v),
      apply: (val) => {
        data.preferences.multi_city_route = val.route;
        data.routing_segments = val.segments;
        data.origin = val.airports[0] || '';
        data.destination = val.airports[val.airports.length - 1] || '';
      },
    },
    {
      key: 'depart_date',
      ask: () => (data.trip_type === 'return' ? 'Outbound travel date?' : 'Travel date?'),
      hint: DATE_HINT,
      validate: validateSmartDate,
      parse: (v) => parseSmartDateOnly(v),
      apply: (val) => { data.depart_date = val; },
    },
    {
      key: 'return_date',
      ask: 'Return travel date?',
      hint: DATE_HINT,
      when: () => data.trip_type === 'return',
      optional: true,
      validate: (v) => (isSkip(v) ? null : validateSmartDate(v)),
      parse: (v) => (isSkip(v) ? null : parseSmartDateOnly(v)),
      apply: (val) => { data.return_date = val; },
    },
    {
      key: 'pax_line',
      ask: 'Passengers (ADT/CHD/INF)?',
      hint: 'One line e.g. 2/1/0 — adults / children under 12 / infants under 2',
      validate: (v) => (parseTicketPaxLine(v) ? null : 'Enter pax like 2/1/0 (≥ 1 adult).'),
      parse: (v) => parseTicketPaxLine(v),
      apply: (val) => { data.pax = { ...data.pax, ...val }; },
    },
    {
      key: 'pref_baggage',
      ask: 'Preferred baggage allowance?',
      hint: '1 cabin · 2 checked 23kg · 3 checked 30kg+ · 4 extra · skip',
      optional: true,
      parse: (v) => parseBaggage(v),
      apply: (val) => { data.preferences.baggage = val; },
    },
  ];
}

export function activeNewTicketStepsV2(data, helpers) {
  const steps = buildNewTicketStepsV2(data, helpers).filter((s) => !s.when || s.when(data));
  return prepareFlowSteps('ticket.new', injectCustomSteps('ticket.new', steps, { mode: 'new', data }));
}

export function buildExtraFromTicketDataV2(data) {
  const pax = {
    adt: Number(data.pax?.adt) || 0,
    stu: 0,
    chd: Number(data.pax?.chd) || 0,
    inf: Number(data.pax?.inf) || 0,
  };
  const extra = {
    flow_version: 'ticket_v2',
    pax,
    preferences: { ...data.preferences },
    per_adt_itineraries: false,
    adt_profiles: null,
    stu_profiles: null,
    adt_pricing: null,
    stu_pricing: null,
    pax_cost_rates: null,
    pax_profit_rates: null,
    pax_rates: null,
    booking_history: [],
  };
  if (data.routing_segments?.length) {
    extra.flight_segments = data.routing_segments.map((s) => ({ ...s }));
  }
  return mergeCustomFieldsIntoExtra(extra, data._customFields);
}

export function buildNewTicketLeadBodyV2(data, helpers = {}) {
  const extra = buildExtraFromTicketDataV2(data);
  const tripType = normalizeTripType(data.trip_type) || 'one_way';
  const pax = extra.pax;
  const totalPax = pax.adt + pax.chd + pax.inf;

  return {
    type: 'ticket',
    name: data.name,
    phone: data.phone,
    source: data.source || 'Call',
    status: 'new',
    agent_id: null,
    inquiry_date: data.inquiry_date || todayISO(),
    followup_date: helpers.defaultFollowupISO?.() || todayISO(),
    origin: data.origin || null,
    destination: data.destination || null,
    depart_date: data.depart_date || null,
    return_date: tripType === 'return' ? (data.return_date || null) : null,
    trip_type: tripType,
    passengers: totalPax > 0 ? totalPax : null,
    quote_amount: null,
    amount: null,
    cost_amount: null,
    extra,
  };
}

export function formatNewTicketSummaryV2(data) {
  const pax = data.pax || {};
  const prefs = data.preferences || {};
  const lines = [
    '<strong>Ticket inquiry</strong>',
    `Contact: ${esc(data.name)} · ${esc(data.phone)}`,
    `Inquiry: ${fmtDate(data.inquiry_date) || todayISO()} · Source: ${esc(data.source || '—')}`,
    `Pax: ${pax.adt || 0} ADT · ${pax.chd || 0} CHD · ${pax.inf || 0} INF`,
  ];
  if (prefs.transit === 'multi' && prefs.multi_city_route) {
    lines.push(`Route: Multi-city · ${esc(prefs.multi_city_route)}`);
  } else {
    let route = `${esc(data.origin || '?')}→${esc(data.destination || '?')}`;
    if (prefs.transit === 'transit' && prefs.transit_via) route += ` via ${esc(prefs.transit_via)}`;
    lines.push(`Route: ${route}`);
  }
  const tt = normalizeTripType(data.trip_type) || 'one_way';
  lines.push(`Trip: ${tt === 'return' ? 'Return' : tt === 'multi' ? 'Multi-city' : 'One way'}`);
  if (data.depart_date) {
    lines.push(`Travel: ${fmtDate(data.depart_date)}${data.return_date ? ` – ${fmtDate(data.return_date)}` : ''}`);
  }
  if (prefs.baggage) lines.push(`Baggage: ${esc(prefs.baggage)}`);
  lines.push('<em>Saved to unassigned pool — a quoting agent can claim this inquiry.</em>');
  return lines.join('\n');
}

/** Sync quotation stage rates into extra for pricing helpers. */
export function syncTicketQuotationRates(extra, rates = {}) {
  const cost = {};
  const profit = {};
  for (const key of ['adt', 'chd', 'inf']) {
    if (rates[`cost_${key}`] != null) cost[key] = Number(rates[`cost_${key}`]) || 0;
    if (rates[`profit_${key}`] != null) profit[key] = Number(rates[`profit_${key}`]) || 0;
  }
  if (Object.keys(cost).length) extra.pax_cost_rates = cost;
  if (Object.keys(profit).length) extra.pax_profit_rates = profit;
  return extra;
}

export function calcTicketQuoteTotals(pax = {}, rates = {}) {
  let cost = 0;
  let profit = 0;
  for (const key of ['adt', 'chd', 'inf']) {
    const n = Number(pax[key]) || 0;
    if (!n) continue;
    cost += n * (Number(rates[`cost_${key}`]) || 0);
    profit += n * (Number(rates[`profit_${key}`]) || 0);
  }
  const roundedCost = Math.round(cost);
  const roundedProfit = Math.round(profit);
  return { cost: roundedCost, profit: roundedProfit, total: roundedCost + roundedProfit };
}

/** Free-text intake from the quick ticket inquiry modal. */
export function ticketIntakeText(lead = {}) {
  const extra = lead.extra || {};
  return String(extra.intake_details || lead.custom_req || '').trim();
}

/** Ticket created via pool intake (details text box, route deferred to quote stage). */
export function isTicketQuickIntake(lead = {}) {
  const extra = lead.extra || {};
  if (extra.flow_version === 'ticket_v2') return true;
  const intake = ticketIntakeText(lead);
  if (!intake) return false;
  const hasRoute = !!(lead.origin || lead.destination || extra.flight_segments?.length);
  return !hasRoute;
}

export function isUnassignedPoolTicket(lead = {}) {
  return !lead.agent_id && lead.type === 'ticket' && lead.status === 'new';
}

/** Placeholder names generated when intake has no customer name. */
export function isAutoTicketCustomerName(name, phone = '') {
  const n = String(name || '').trim();
  if (!n) return true;
  if (/^customer\s*[·•\-|]\s*/i.test(n)) return true;
  const digits = String(phone).replace(/\D/g, '');
  const nameDigits = n.replace(/\D/g, '');
  if (digits && nameDigits && (nameDigits.includes(digits.slice(-8)) || digits.includes(nameDigits.slice(-8)))) {
    return true;
  }
  return false;
}

/** Custom status badge for new ticket inquiries in the pool workflow. */
export function detailTicketStatusLabel(lead = {}) {
  if (lead.type !== 'ticket' || lead.status !== 'new') return null;
  if (!lead.agent_id) return 'New inquiry · Unassigned';
  return `Assigned · ${lead.agent_name || 'Agent'}`;
}
